/**
 * The caller's AI access level for a customer (#1537), and the Manager's
 * "Enforce AI access levels" switch.
 *
 * The feature→level map comes from the server (GET /ai-access/:customer/me),
 * so no level is written down in the frontend. can(feature) mirrors the
 * server's check to hide or grey a control; the server still refuses, because
 * a hidden button is not security. Until the answer arrives, or if it fails,
 * can() says yes: the server's refusal then reaches the person through
 * extractErrorMessage (utils/aiAccess.ts).
 *
 * A used team budget (#1226) also makes can() say no, from the same /me
 * answer, so a greyed control and its tooltip need no second request. So
 * does a team with no budget now (budget.no_budget: never had a budget year,
 * or its year ended or has not started), whose /me answer marks both kinds
 * exhausted.
 *
 * customer is the customer NAME (account.customer.name).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'
import type { AIAccessFeature, AIAccessFeatureInfo, AIAccessRequestResponse, AIRealmAdmin, CustomerAIAccess, MyAIAccess, MyAIBudget } from '../types/gotypes'
import { noBudgetReason, refusalSentence } from '../utils/aiAccess'

export const aiAccessQueryKey = (customer: string) => ['ai-access', customer.toLowerCase(), 'me']

export interface UseAiAccessReturn {
  /** The caller's level, 1–4; 4 while the switch is off. Undefined until loaded. */
  level: number | undefined
  /** Whether the customer's switch is on. */
  enforced: boolean
  loaded: boolean
  /** The /me request is still in flight. False once it answered or failed, so a gate waiting on it cannot hang. */
  loading: boolean
  /** May the caller use this feature now? No when the level is too low or the team's budget is used. */
  can: (feature: AIAccessFeature) => boolean
  /** Does the caller's LEVEL allow this feature, ignoring the team budget? For showing an entry point: a used budget greys the control inside, it does not hide the door. */
  levelAllows: (feature: AIAccessFeature) => boolean
  /** Is the caller's team budget used for this feature? A batch feature stops before an interactive one. */
  budgetExhausted: (feature: AIAccessFeature) => boolean
  /** The whole feature→level map, for a page that shows every level what it adds (#1537 PR D). Empty until loaded. */
  features: AIAccessFeatureInfo[]
  /** The refusal sentence for a feature, for a tooltip on a greyed control: the budget sentence when the level allows but the budget does not. */
  refusalFor: (feature: AIAccessFeature) => string
  /** Manager only: turn enforcement on or off for the customer. */
  handleSetEnforced: (enforced: boolean) => Promise<CustomerAIAccess>
  isSettingEnforced: boolean
  /** The caller's budget team, when they're in one. Undefined otherwise. */
  teamName: string | undefined
  /** The caller's team budget (price, with remaining_credits and the unit); undefined when none applies. */
  budget: MyAIBudget | undefined
  /** No budget year covers now for the caller's team (never had one, ended, or not started), so every AI feature is refused. */
  noBudget: boolean
  /** The customer's Realm admins, whom a refusal names. */
  admins: AIRealmAdmin[]
  /** The managers of the caller's team, switch on or off; empty when they are in none. */
  teamManagers: AIRealmAdmin[]
  /** Ids of the teams the caller is appointed over at this customer; empty for most people. */
  manages: string[]
  /** The caller is the stats reviewer of the customer or one of its jobs (#1652), so Admin shows Stats review. */
  statsReviewer: boolean
  /** A Manager named the caller a reviewer of a guide (migration 000190): Guided conversations is theirs to open. */
  guideReviewer: boolean
  /** The caller has the Finance view at this customer (a Manager, or granted it), so pages show the hours-saved figure. */
  financeView: boolean
  /** "Request access" (#1537 PR D): asks the customer's Realm admins to raise your level. */
  handleRequestAccess: (args: { feature?: AIAccessFeature; message?: string }) => Promise<AIAccessRequestResponse>
  isRequestingAccess: boolean
}

export const useAiAccess = (customer: string | undefined): UseAiAccessReturn => {
  const queryClient = useQueryClient()
  const name = (customer || '').trim()
  const key = aiAccessQueryKey(name)

  const query = useQuery<MyAIAccess, Error>({
    queryKey: key,
    queryFn: async () =>
      (await axios.get<MyAIAccess>(`${API_BASE_URL}/ai-access/${encodeURIComponent(name)}/me`)).data,
    enabled: name !== '',
    staleTime: 60_000,
    retry: false,
  })

  const setEnforcedMutation = useMutation<CustomerAIAccess, Error, boolean>({
    mutationFn: async (enforced) =>
      (await axios.put<CustomerAIAccess>(`${API_BASE_URL}/admin/customers/${encodeURIComponent(name)}/ai-access`, { enforced })).data,
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  })

  const requestAccessMutation = useMutation<AIAccessRequestResponse, Error, { feature?: AIAccessFeature; message?: string }>({
    mutationFn: async ({ feature, message }) =>
      (await axios.post<AIAccessRequestResponse>(`${API_BASE_URL}/ai-access/${encodeURIComponent(name)}/request`, { feature, message })).data,
  })

  const data = query.data
  const featureInfo = (feature: AIAccessFeature) => data?.features?.find((f) => f.feature === feature)
  // A feature the server did not list needs the top level, as it does on the
  // server, and is a batch feature.
  const levelAllows = (feature: AIAccessFeature) => !data || data.level >= (featureInfo(feature)?.level ?? 4)
  const budgetExhausted = (feature: AIAccessFeature) => {
    const exhausted = data?.budget?.exhausted
    if (!exhausted) return false
    const kind = featureInfo(feature)?.kind
    // A free feature (the Help pane) is never stopped by a budget.
    if (kind === 'free') return false
    return kind === 'interactive' ? exhausted.interactive : exhausted.batch
  }

  return {
    level: data?.level,
    enforced: !!data?.enforced,
    loaded: !!data,
    loading: query.isPending && name !== '',
    can: (feature) => levelAllows(feature) && !budgetExhausted(feature),
    levelAllows,
    budgetExhausted,
    features: data?.features ?? [],
    refusalFor: (feature) => {
      const reason = !levelAllows(feature)
        ? data?.source === 'no_team' ? 'no_team' : 'level_too_low'
        : data?.budget?.no_budget ? noBudgetReason(data.budget.state)
          : budgetExhausted(feature) ? 'budget_exhausted' : 'level_too_low'
      return refusalSentence({
        reason, admins: data?.admins, team_managers: data?.team_managers,
        starts_at: data?.budget?.starts_at, ends_at: data?.budget?.ends_at,
      })
    },
    handleSetEnforced: (enforced) => setEnforcedMutation.mutateAsync(enforced),
    isSettingEnforced: setEnforcedMutation.isPending,
    teamName: data?.team_name,
    budget: data?.budget,
    noBudget: !!data?.budget?.no_budget,
    admins: data?.admins ?? [],
    teamManagers: data?.team_managers ?? [],
    manages: data?.manages ?? [],
    statsReviewer: !!data?.stats_reviewer,
    guideReviewer: !!data?.guide_reviewer,
    financeView: !!data?.finance_view,
    handleRequestAccess: (args) => requestAccessMutation.mutateAsync(args),
    isRequestingAccess: requestAccessMutation.isPending,
  }
}

/**
 * Manager only: the billing address AutoCoder and connector work is billed to.
 * Saved on its own; the PUT carries billing_email and never the switch.
 */
export const useBillingAddress = (customer: string | undefined, enabled: boolean) => {
  const queryClient = useQueryClient()
  const name = (customer || '').trim()
  const key = ['ai-access', name.toLowerCase(), 'admin']
  const url = `${API_BASE_URL}/admin/customers/${encodeURIComponent(name)}/ai-access`

  const query = useQuery<CustomerAIAccess, Error>({
    queryKey: key,
    queryFn: async () => (await axios.get<CustomerAIAccess>(url)).data,
    enabled: enabled && name !== '',
    retry: false,
  })

  const mutation = useMutation<CustomerAIAccess, Error, string>({
    mutationFn: async (billing_email) => (await axios.put<CustomerAIAccess>(url, { billing_email })).data,
    onSuccess: (row) => queryClient.setQueryData(key, row),
  })

  return {
    billingEmail: query.data?.billing_email ?? '',
    loaded: !!query.data,
    handleSetBillingEmail: (email: string) => mutation.mutateAsync(email),
    isSavingBillingEmail: mutation.isPending,
  }
}

export default useAiAccess
